import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "@/layouts/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { setToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";

/**
 * Email verification screen.
 *
 * Reads ?email=... from the query string (set by Register after a successful
 * signup, or by Login when the backend returns EMAIL_NOT_VERIFIED). The user
 * enters the 6-digit OTP they received and we POST /auth/verify-email.
 *
 * On success the backend returns the same payload as /auth/login (user data
 * + access_token + httpOnly cookies). We persist the token and bounce to
 * /onboarding so the new user lands on the module-selection flow exactly
 * like the previous "auto-login at signup" UX, just one step later.
 */
export default function VerifyEmail() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const emailFromQuery = (params.get("email") || "").trim();
  const [email] = useState(emailFromQuery);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const inputRef = useRef(null);

  // Auto-focus the OTP field for one-handed mobile UX.
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Countdown for the "Reinvia codice" button. We also start one immediately
  // after landing on the page since the registration already sent the first
  // email — prevents users from re-spamming the moment they arrive.
  useEffect(() => {
    setCooldown(60);
  }, []);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!emailFromQuery) {
    // Guard: this page is only meaningful with an email in the URL.
    // Anything else means the user landed here by mistake.
    return (
      <AuthLayout title="Verifica email" subtitle="Indirizzo mancante">
        <div className="text-sm text-neutral-700 leading-relaxed">
          Manca l'indirizzo email da verificare. Torna alla{" "}
          <Link to="/register" className="text-primary underline hover:no-underline">
            registrazione
          </Link>{" "}
          o{" "}
          <Link to="/login" className="text-primary underline hover:no-underline">
            accedi
          </Link>.
        </div>
      </AuthLayout>
    );
  }

  const onlyDigits = (s) => s.replace(/\D/g, "").slice(0, 6);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError("Inserisci il codice a 6 cifre.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email, code });
      if (data?.access_token) setToken(data.access_token);
      await refresh();
      toast.success("Email verificata. Benvenuta!");
      navigate(data?.onboarded ? "/app/dashboard" : "/onboarding", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError("");
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success("Nuovo codice inviato. Controlla la posta.");
      setCooldown(60);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend's 429 message already carries a precise "wait X seconds".
      setError(formatApiError(detail) || err.message);
      // If the backend told us to wait, parse the cooldown so the button reflects it.
      const m = typeof detail === "string" ? detail.match(/(\d+)\s*sec/i) : null;
      if (m) setCooldown(parseInt(m[1], 10) || 60);
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout title="Verifica il tuo indirizzo email" subtitle={null}>
      <div
        className="mb-6 rounded-sm border px-4 py-3 flex items-start gap-3"
        style={{
          backgroundColor: "rgba(200,169,126,0.08)",
          borderColor: "rgba(200,169,126,0.4)",
        }}
        data-testid="verify-email-info"
      >
        <MailCheck className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "#7B6A45" }} strokeWidth={1.5} />
        <p className="text-sm leading-relaxed" style={{ color: "#7B6A45" }}>
          Ti abbiamo inviato un codice di 6 cifre all'indirizzo{" "}
          <strong className="font-medium">{email}</strong>.<br />
          Inseriscilo qui sotto per attivare il tuo account.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-5" data-testid="verify-email-form">
        <div>
          <Label htmlFor="otp" className="label-uppercase">Codice di verifica</Label>
          <Input
            id="otp"
            ref={inputRef}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(onlyDigits(e.target.value))}
            placeholder="••••••"
            className="mt-2 rounded-sm h-14 text-center text-2xl tracking-[0.5em] font-mono"
            data-testid="verify-email-code-input"
          />
          <p className="text-xs text-neutral-500 mt-1.5">
            Il codice scade tra 10 minuti.
          </p>
        </div>

        {error && (
          <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 p-3 rounded-sm" data-testid="verify-email-error">
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={busy || code.length !== 6}
          className="w-full h-11 rounded-sm"
          data-testid="verify-email-submit-button"
        >
          {busy ? "Verifica in corso…" : "Verifica account"}
        </Button>

        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={onResend}
            disabled={cooldown > 0 || resending}
            className="text-sm text-primary hover:underline disabled:text-neutral-400 disabled:no-underline disabled:cursor-not-allowed"
            data-testid="verify-email-resend-button"
          >
            {resending
              ? "Invio…"
              : cooldown > 0
              ? `Reinvia codice (${cooldown}s)`
              : "Reinvia codice"}
          </button>
          <Link
            to="/login"
            className="text-sm text-neutral-500 hover:text-primary hover:underline"
            data-testid="verify-email-back-to-login"
          >
            Torna al login
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
